/* global document, fetch, setTimeout, window */

const DELIVERY_LABELS = ['Czas dostawy', 'Delivery time'];

function getDefaultDeliveryLabel() {
  const htmlLang = (document.documentElement?.getAttribute('lang') || window.Shopify?.locale || 'pl').toLowerCase();
  return htmlLang.startsWith('en') ? 'Delivery time' : 'Czas dostawy';
}

function normalizeText(value) {
  return String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function getDomCustomerNote() {
  if (typeof document === 'undefined') return null;
  const textareas = Array.from(document.querySelectorAll('textarea[name="note"]'));
  if (!textareas.length) return null;
  if (document.activeElement && textareas.includes(document.activeElement)) {
    return document.activeElement.value;
  }
  const filled = textareas.find((el) => el.value && el.value.trim().length > 0);
  return filled ? filled.value : textareas[0].value;
}

window.iDreamCart = {
  cleanCustomerNote(rawNote, customerNoteAttr) {
    const candidate =
      typeof customerNoteAttr === 'string' && customerNoteAttr.length > 0 ? customerNoteAttr : String(rawNote || '');
    return candidate
      .split('\n')
      .filter((line) => {
        const trimmed = line.trim();
        return !DELIVERY_LABELS.some((label) => trimmed.startsWith(`${label} -`));
      })
      .join('\n')
      .trim();
  },

  buildDeliveryLines(cartData) {
    if (!cartData || !Array.isArray(cartData.items)) return [];

    const defaultLabel = getDefaultDeliveryLabel();
    const domRowsByUuid = new Map();
    if (typeof document !== 'undefined') {
      document.querySelectorAll('tr.cart-item').forEach((row) => {
        const uuid = row.getAttribute('data-uuid');
        if (uuid) {
          domRowsByUuid.set(uuid, row);
        }
      });
    }

    const deliveryLines = [];

    cartData.items.forEach((item, index) => {
      const props = item.properties || {};
      if (
        props._aplcr_main_product_sku ||
        props._sw_main_product_sku ||
        props._gwp_main_product_sku ||
        props._is_bundle_item ||
        props._trade_in_parent_id
      ) {
        return;
      }

      const domRow =
        (props._uuid && domRowsByUuid.get(String(props._uuid))) ||
        (typeof document?.getElementById === 'function' ? document.getElementById(`CartItem-${index + 1}`) : null);
      let deliveryLabel = defaultLabel;
      let deliveryMessage = '';

      for (const candidateLabel of DELIVERY_LABELS) {
        if (props[candidateLabel]) {
          deliveryLabel = candidateLabel;
          deliveryMessage = normalizeText(props[candidateLabel]);
          break;
        }
      }

      const domLabel = normalizeText(domRow?.getAttribute('data-delivery-label'));
      const domMessage = normalizeText(domRow?.getAttribute('data-delivery-message'));
      const domWarehouse = normalizeText(domRow?.getAttribute('data-virtual-warehouse'));

      if (domLabel) {
        deliveryLabel = domLabel;
      }
      if (domMessage) {
        deliveryMessage = domMessage;
      }

      const virtualWarehouse = domRow ? domWarehouse : normalizeText(props._virtual_warehouse);

      if (deliveryMessage) {
        const itemTitle = normalizeText(item.product_title || item.title);
        const sku = normalizeText(item.sku || props.SKU);
        const skuSuffix = sku ? ` (${sku})` : '';
        const warehouseSuffix = virtualWarehouse ? ` [${virtualWarehouse}]` : '';
        deliveryLines.push(`${deliveryLabel} - ${itemTitle}${skuSuffix}: ${deliveryMessage}${warehouseSuffix}`);
      }
    });

    return deliveryLines;
  },

  async getLineDraft({ cart, key, line, uuid, omit = [] }) {
    const currentCart =
      cart ||
      (await fetch(`${window.Shopify.routes.root}cart.js`).then((response) => {
        if (!response.ok) throw new Error('Unable to read the current cart.');
        return response.json();
      }));
    const items = currentCart.items || [];
    const currentLine =
      (key && items.find((item) => item.key === key)) ||
      (uuid && items.find((item) => item.properties?._uuid === uuid)) ||
      items[Number(line) - 1];

    if (!currentLine) throw new Error('Unable to resolve the current cart line.');

    const properties = { ...currentLine.properties };
    omit.forEach((property) => delete properties[property]);

    return { key: currentLine.key, properties };
  },

  getDeliveryPropertiesFromForm({ isBundle = false } = {}) {
    try {
      const msgInput = document.getElementById('main-pro-delivery-message');
      const warehouseInput = document.getElementById('main-pro-virtual-warehouse');
      const label = normalizeText(msgInput?.getAttribute('data-label')) || getDefaultDeliveryLabel();

      let message = '';
      if (msgInput) {
        const rawMessage = isBundle ? msgInput.getAttribute('data-bundle-message') || msgInput.value : msgInput.value;
        message = normalizeText(rawMessage);
      }

      if (!message) {
        const wrapper = document.querySelector('.apl-section-shipping__message-wrapper');
        message = normalizeText(wrapper?.textContent);
      }

      const warehouse = isBundle ? '' : normalizeText(warehouseInput?.value);
      const result = {};

      if (message) {
        result[label] = message;
      }
      if (warehouse) {
        result._virtual_warehouse = warehouse;
      }

      const npiInput = document.getElementById('main-pro-npi-restricted');
      const excludedInput = document.getElementById('main-pro-excluded-warehouses');
      if (npiInput && npiInput.value === 'true') {
        result._pickup_restricted = 'true';
        if (excludedInput?.value) {
          result._excluded_warehouses = excludedInput.value;
        }
      }

      return result;
    } catch {
      return {};
    }
  },

  getDeliveryPropertiesFromCartRow(rowElement) {
    try {
      const el = rowElement?.jquery ? rowElement[0] : rowElement;
      if (!el) return {};

      const label = normalizeText(el.getAttribute('data-delivery-label')) || getDefaultDeliveryLabel();
      const message = normalizeText(el.getAttribute('data-delivery-message'));
      const warehouse = normalizeText(el.getAttribute('data-virtual-warehouse'));
      const isRestricted = el.getAttribute('data-pickup-restricted');
      const excludedWarehouses = el.getAttribute('data-excluded-warehouses');
      const result = {};

      if (message) {
        result[label] = message;
      }
      if (warehouse) {
        result._virtual_warehouse = warehouse;
      }
      if (isRestricted === 'true') {
        result._pickup_restricted = 'true';
        if (excludedWarehouses) {
          result._excluded_warehouses = excludedWarehouses;
        }
      }

      return result;
    } catch {
      return {};
    }
  },

  async syncDeliveryMetadata(cartData) {
    try {
      const rootUrl = window.Shopify?.routes?.root || '/';
      const currentCart =
        cartData && Array.isArray(cartData.items)
          ? cartData
          : await fetch(`${rootUrl}cart.js`).then((response) => (response.ok ? response.json() : null));

      if (!currentCart || !Array.isArray(currentCart.items)) {
        return null;
      }

      const domRowsByUuid = new Map();
      if (typeof document !== 'undefined') {
        document.querySelectorAll('tr.cart-item').forEach((row) => {
          const uuid = row.getAttribute('data-uuid');
          if (uuid) {
            domRowsByUuid.set(uuid, row);
          }
        });
      }

      const defaultLabel = getDefaultDeliveryLabel();
      const warehouses = new Set();
      const lineUpdates = [];
      let hasPickupRestriction = false;
      const excludedWarehouses = new Set();

      currentCart.items.forEach((item, index) => {
        const props = item.properties || {};
        if (
          props._aplcr_main_product_sku ||
          props._sw_main_product_sku ||
          props._gwp_main_product_sku ||
          props._is_bundle_item ||
          props._trade_in_parent_id
        ) {
          return;
        }

        const domRow =
          (props._uuid && domRowsByUuid.get(String(props._uuid))) ||
          (typeof document?.getElementById === 'function' ? document.getElementById(`CartItem-${index + 1}`) : null);
        let deliveryLabel = defaultLabel;

        for (const candidateLabel of DELIVERY_LABELS) {
          if (props[candidateLabel]) {
            deliveryLabel = candidateLabel;
            break;
          }
        }

        const domLabel = normalizeText(domRow?.getAttribute('data-delivery-label'));
        const domMessage = normalizeText(domRow?.getAttribute('data-delivery-message'));
        const domWarehouse = normalizeText(domRow?.getAttribute('data-virtual-warehouse'));

        if (domLabel) {
          deliveryLabel = domLabel;
        }

        const virtualWarehouse = domRow ? domWarehouse : normalizeText(props._virtual_warehouse);

        const isRestricted =
          props._pickup_restricted === 'true' || domRow?.getAttribute('data-pickup-restricted') === 'true';
        const excludedVal = props._excluded_warehouses || domRow?.getAttribute('data-excluded-warehouses');

        if (isRestricted) {
          hasPickupRestriction = true;
          if (excludedVal) {
            try {
              const parsed = JSON.parse(excludedVal);
              if (Array.isArray(parsed)) {
                parsed.forEach((w) => excludedWarehouses.add(String(w)));
              } else if (typeof parsed === 'object' && parsed !== null) {
                const list = parsed.excluded_warehouses || parsed.excluded_locations || Object.values(parsed);
                if (Array.isArray(list)) list.forEach((w) => excludedWarehouses.add(String(w)));
              }
            } catch {
              String(excludedVal)
                .split(',')
                .forEach((w) => {
                  const trimmed = w.trim();
                  if (trimmed) excludedWarehouses.add(trimmed);
                });
            }
          }
        }

        if (
          domRow &&
          domMessage &&
          (normalizeText(props[deliveryLabel]) !== domMessage ||
            normalizeText(props._virtual_warehouse) !== domWarehouse)
        ) {
          const nextProps = { ...props, [deliveryLabel]: domMessage };
          if (domWarehouse) {
            nextProps._virtual_warehouse = domWarehouse;
          } else {
            delete nextProps._virtual_warehouse;
          }
          lineUpdates.push({
            id: item.key,
            quantity: item.quantity,
            properties: nextProps,
          });
        }

        if (virtualWarehouse) {
          warehouses.add(virtualWarehouse);
        }
      });

      for (const update of lineUpdates) {
        await fetch(`${rootUrl}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(update),
        });
      }

      const currentNote = String(currentCart.note || '');
      const cleanedNote = this.cleanCustomerNote(currentNote, currentCart.attributes?._customer_note);

      if (cleanedNote !== currentNote && typeof document !== 'undefined') {
        document.querySelectorAll('textarea[name="note"]').forEach((el) => {
          if (el.value === currentNote || el.value !== cleanedNote) {
            el.value = cleanedNote;
          }
        });
      }

      const nextWarehouseAttr = Array.from(warehouses).join(', ');
      const nextNpiRestrictedAttr = hasPickupRestriction ? 'true' : '';
      const nextNpiExcludedAttr = Array.from(excludedWarehouses).join(', ');
      const currentAttributes = currentCart.attributes || {};

      const needsNoteUpdate = cleanedNote !== currentNote;
      const needsWarehouseUpdate = String(currentAttributes.virtual_warehouse || '') !== nextWarehouseAttr;
      const needsNpiRestrictedUpdate = String(currentAttributes.npi_pickup_restricted || '') !== nextNpiRestrictedAttr;
      const needsNpiExcludedUpdate = String(currentAttributes.npi_excluded_warehouses || '') !== nextNpiExcludedAttr;

      if (!needsNoteUpdate && !needsWarehouseUpdate && !needsNpiRestrictedUpdate && !needsNpiExcludedUpdate) {
        return currentCart;
      }

      const updatePayload = {
        attributes: {
          ...currentAttributes,
          virtual_warehouse: nextWarehouseAttr,
          npi_pickup_restricted: nextNpiRestrictedAttr,
          npi_excluded_warehouses: nextNpiExcludedAttr,
        },
      };

      if (needsNoteUpdate) {
        updatePayload.note = cleanedNote;
      }

      const response = await fetch(`${rootUrl}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(updatePayload),
      });

      return response.ok ? response.json() : currentCart;
    } catch {
      return null;
    }
  },

  async prepareCheckoutCart(cartData) {
    try {
      const rootUrl = window.Shopify?.routes?.root || '/';
      const currentCart =
        cartData && Array.isArray(cartData.items)
          ? cartData
          : await fetch(`${rootUrl}cart.js`).then((response) => (response.ok ? response.json() : null));

      if (!currentCart || !Array.isArray(currentCart.items)) {
        return null;
      }

      const domNote = getDomCustomerNote();
      const customerNote =
        domNote !== null
          ? this.cleanCustomerNote(domNote)
          : this.cleanCustomerNote(currentCart.note, currentCart.attributes?._customer_note);

      const deliveryLines = this.buildDeliveryLines(currentCart);
      const deliveryText = deliveryLines.join('\n');
      const finalNote = [customerNote, deliveryText].filter(Boolean).join('\n\n');

      const currentAttributes = currentCart.attributes || {};
      const needsNoteUpdate = finalNote !== String(currentCart.note || '');
      const needsCustomerNoteAttrUpdate = String(currentAttributes._customer_note || '') !== customerNote;

      if (!needsNoteUpdate && !needsCustomerNoteAttrUpdate) {
        return currentCart;
      }

      const response = await fetch(`${rootUrl}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          note: finalNote,
          attributes: {
            ...currentAttributes,
            _customer_note: customerNote,
          },
        }),
      });

      return response.ok ? response.json() : currentCart;
    } catch {
      return null;
    }
  },

  async restoreCustomerCart() {
    try {
      const rootUrl = window.Shopify?.routes?.root || '/';
      const currentCart = await fetch(`${rootUrl}cart.js`).then((response) => (response.ok ? response.json() : null));

      if (!currentCart || !Array.isArray(currentCart.items)) {
        return null;
      }

      const currentNote = String(currentCart.note || '');
      const cleanedNote = this.cleanCustomerNote(currentNote, currentCart.attributes?._customer_note);

      if (typeof document !== 'undefined') {
        document.querySelectorAll('textarea[name="note"]').forEach((el) => {
          if (el.value !== cleanedNote) {
            el.value = cleanedNote;
          }
        });
      }

      if (cleanedNote === currentNote) {
        return currentCart;
      }

      const response = await fetch(`${rootUrl}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          note: cleanedNote,
        }),
      });

      return response.ok ? response.json() : currentCart;
    } catch {
      return null;
    }
  },

  initCheckoutInterception() {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (typeof document.addEventListener !== 'function' || typeof window.addEventListener !== 'function') return;
    if (window._idreamCheckoutInterceptionInit) return;
    window._idreamCheckoutInterceptionInit = true;

    document.addEventListener(
      'input',
      (event) => {
        if (event.target && event.target.matches && event.target.matches('textarea[name="note"]')) {
          const val = event.target.value;
          document.querySelectorAll('textarea[name="note"]').forEach((el) => {
            if (el !== event.target && el.value !== val) {
              el.value = val;
            }
          });
        }
      },
      true,
    );

    window.addEventListener('pageshow', () => {
      this.restoreCustomerCart();
    });

    document.addEventListener(
      'click',
      async (event) => {
        const trigger = event.target?.closest?.(
          'button[name="checkout"], input[name="checkout"], #checkout, .cart__checkout-button, .apl-section-minicart-actions-checkout, a[href$="/checkout"], a[href*="/checkout?"]',
        );
        if (!trigger) return;
        if (trigger.dataset?.idreamCheckoutReady === 'true') return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation?.();

        trigger.dataset.idreamCheckoutReady = 'true';

        try {
          await Promise.race([this.prepareCheckoutCart(), new Promise((resolve) => setTimeout(resolve, 1500))]);
        } catch {
          // Proceed anyway
        }

        if (trigger.tagName === 'A' && trigger.href) {
          window.location.href = trigger.href;
        } else if (trigger.form) {
          trigger.click();
        } else {
          const rootUrl = window.Shopify?.routes?.root || '/';
          window.location.href = `${rootUrl}checkout`;
        }
      },
      true,
    );

    document.addEventListener(
      'submit',
      async (event) => {
        const form = event.target;
        if (!form || !form.action || !form.action.includes('/cart')) return;
        if (form.dataset?.idreamCheckoutReady === 'true') return;

        const submitter = event.submitter;
        const isCheckout =
          !submitter ||
          submitter.name === 'checkout' ||
          submitter.id === 'checkout' ||
          submitter.classList?.contains('cart__checkout-button');
        if (!isCheckout) return;

        event.preventDefault();
        event.stopPropagation();

        form.dataset.idreamCheckoutReady = 'true';

        try {
          await Promise.race([this.prepareCheckoutCart(), new Promise((resolve) => setTimeout(resolve, 1500))]);
        } catch {
          // Proceed anyway
        }

        const rootUrl = window.Shopify?.routes?.root || '/';
        window.location.href = `${rootUrl}checkout`;
      },
      true,
    );
  },
};

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.iDreamCart?.initCheckoutInterception?.();
    });
  } else {
    window.iDreamCart?.initCheckoutInterception?.();
  }
}
